import React from 'react'
import { Modal, ModalHeader, ModalBody, Button, ModalFooter } from 'reactstrap'
import intl from 'react-intl-universal'
import { useAsync } from 'react-async'
import { useSelector, useDispatch } from 'react-redux'
import { isAfter } from 'date-fns'
import { zonedTimeToUtc } from 'date-fns-tz'

import { DatePickerInput } from '~/reactapp/shared/DatePicker'
import { PublishErrorProps, ButtonProps } from './types'
import { EllipsisReduxStore } from '~/types'
import {
  unpublishStoryPubstack,
  updateDraftRevision,
  scheduleUnpublish,
} from '~/api/story'
import { AppError } from '~/api/AppError'
import {
  setDocumentAction,
  setDraftRevisionAction,
  setStoryAns,
} from '~/redux/storyData'
import useUnpublishDate from './usePublishDate'
import LocaleUtils from '~/reactapp/shared/DatePicker/localeUtils'

interface UnpublishButtonProps extends PublishErrorProps {
  /**
   * initial value for the unpublish date picker
   */
  defaultTime: Date
  /**
   * error if attempting to unpublish before this date
   */
  validationDate: Date
  use24Hour: boolean
  onClose: () => void
  fetchScheduledEvents: () => void
}

export default function UnpublishModal({
  defaultTime,
  validationDate,
  handleError,
  story,
  use24Hour,
  onClose,
  fetchScheduledEvents,
}: UnpublishButtonProps & ButtonProps) {
  const dispatch = useDispatch()
  const {
    date: selectedUnpublishDate,
    setDate: setSelectedUnpublishDate,
    isDateNow: isUnpublishingNow,
    relativeNow,
  } = useUnpublishDate(defaultTime)

  const { timezone: tz } = useSelector((state: EllipsisReduxStore) => ({
    timezone: state.settings.timezone,
  }))

  const isUnpublishDateValid = isAfter(
    selectedUnpublishDate,
    LocaleUtils.relativeDate(validationDate)
  )

  // SCHEDULE UNPUBLISH
  const {
    run: runScheduleDocumentUnpublish,
    isLoading: isScheduleDocumentUnpublishLoading,
  } = useAsync({
    deferFn: async () => {
      const scheduleUnpublishResult = await scheduleUnpublish(
        story._id,
        zonedTimeToUtc(selectedUnpublishDate, tz).toISOString()
      )
      return scheduleUnpublishResult
    },
    onReject: (error: AppError) => {
      console.error('UnpublishModal error', error)
      handleError(error.errorKey as string)
    },
    onResolve: () => {
      fetchScheduledEvents()
      onClose()
    },
  })

  // UNPUBLISH
  const {
    run: runUnpublishPubstack,
    isLoading: isUnpublishPubstackLoading,
  } = useAsync({
    deferFn: async () => {
      const unpublishResult = await unpublishStoryPubstack(story._id, dispatch)

      const {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        display_date,
        ...storyNoDisplayDate
      } = unpublishResult.legacyStory
      unpublishResult.revision.ans.additional_properties = {
        ...unpublishResult.revision.ans.additional_properties,
        is_published: false,
        has_published_copy: false,
      }
      const updateResult = await updateDraftRevision(
        unpublishResult.document.id,
        storyNoDisplayDate,
        dispatch
      )

      return updateResult
    },
    onReject: (error: AppError) => {
      console.error('UnpublishModal error', error)
      handleError(error.errorKey as string)
    },
    onResolve: (data) => {
      dispatch(setDocumentAction(data.document))
      dispatch(setDraftRevisionAction(data.revision))
      dispatch(
        setStoryAns({
          storyAns: data.legacyStory,
          // Omit isDirty set if action is unpublish
        })
      )
      onClose()
    },
  })

  const isLoading =
    isUnpublishPubstackLoading || isScheduleDocumentUnpublishLoading

  return (
    <Modal isOpen toggle={onClose} className="bootstrap4">
      <ModalHeader toggle={onClose}>
        {intl.get('publishActions.unpublishModal.unpublishStory')}
      </ModalHeader>
      <ModalBody>
        <label htmlFor="displayField">
          {intl.get('publishActions.unpublishModal.unpublishTimeLabel')}
        </label>
        <DatePickerInput
          value={selectedUnpublishDate}
          onChange={(val) => {
            setSelectedUnpublishDate(val)
          }}
          disabledDays={{ before: relativeNow }}
          quickDates={[relativeNow]}
          use24Hour={use24Hour}
        />
        {!isUnpublishDateValid && (
          <p
            className="invalid-feedback"
            style={{ display: 'block', textAlign: 'right' }}
          >
            {intl.get('publishActions.unpublishModal.error')}
          </p>
        )}
      </ModalBody>
      <ModalFooter>
        <Button outline={true} onClick={onClose}>
          {intl.get('publishActions.unscheduleModal.cancelButton')}
        </Button>
        <Button
          disabled={isLoading || !isUnpublishDateValid}
          color="danger"
          data-testid="modal--btn-unpublish"
          onClick={() => {
            if (!isUnpublishingNow) {
              runScheduleDocumentUnpublish()
              return
            }

            runUnpublishPubstack()
          }}
        >
          {intl.get(
            isUnpublishingNow
              ? 'publishActions.unpublishModal.unpublishButton'
              : 'publishActions.unpublishModal.scheduleUnpublishButton'
          )}
        </Button>
      </ModalFooter>
    </Modal>
  )
}
